import {
  Boxes,
  Link2,
  Mail,
  MessageSquare,
  Plug,
  ScrollText,
  Sparkles,
  Store,
  Truck,
  Unplug,
  Wallet,
  Wrench,
} from 'lucide-react'
import type { ComponentType } from 'react'

import { EmptyIntegrations, PageContainer, PageHeader } from '@/components/common'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
} from '@/components/ui'
import { cn } from '@/utils'

import { useIntegrationCenter } from '../hooks'
import {
  INTEGRATION_CATEGORIES,
  INTEGRATION_STATUSES,
  type IntegrationCategory,
  type IntegrationRecord,
  type IntegrationStatus,
} from '../IntegrationTypes'

const ICON_MAP: Record<string, ComponentType<{ className?: string }>> = {
  truck: Truck,
  store: Store,
  sparkles: Sparkles,
  message: MessageSquare,
  boxes: Boxes,
  wallet: Wallet,
  mail: Mail,
  plug: Plug,
}

export function IntegrationCenterPage() {
  const {
    items,
    total,
    categoria,
    setCategoria,
    status,
    setStatus,
    query,
    setQuery,
    toast,
    setToast,
    runAction,
  } = useIntegrationCenter()

  return (
    <PageContainer className="max-w-6xl">
      <PageHeader
        title="Integration Center"
        description="Cadastro central de integrações. Nada fica hardcoded — login real em sprints futuros."
      />

      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="grid flex-1 gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <p className="text-muted-foreground text-xs font-medium">Buscar</p>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Nome, categoria, tipo…"
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-muted-foreground text-xs font-medium">Categoria</p>
            <select
              className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
              value={categoria}
              onChange={(e) => setCategoria(e.target.value as IntegrationCategory | 'Todas')}
            >
              <option value="Todas">Todas</option>
              {INTEGRATION_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <p className="text-muted-foreground text-xs font-medium">Status</p>
            <select
              className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value as IntegrationStatus | 'Todos')}
            >
              <option value="Todos">Todos</option>
              {INTEGRATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-muted-foreground text-xs tabular-nums">
          {items.length} de {total} integração(ões)
        </p>
      </div>

      {toast ? (
        <div
          role="status"
          className="border-border bg-muted/40 mb-4 flex items-start justify-between gap-3 rounded-lg border px-3 py-2.5 text-sm"
        >
          <span>{toast}</span>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground text-xs underline"
            onClick={() => setToast(null)}
          >
            Fechar
          </button>
        </div>
      ) : null}

      {items.length === 0 ? (
        <EmptyIntegrations
          description="Ajuste os filtros ou cadastre novas integrações no Integration Center."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((item) => (
            <IntegrationCard key={item.id} item={item} onAction={runAction} />
          ))}
        </div>
      )}
    </PageContainer>
  )
}

function IntegrationCard({
  item,
  onAction,
}: {
  item: IntegrationRecord
  onAction: (
    action: 'edit' | 'test' | 'connect' | 'disconnect' | 'logs',
    id: string,
  ) => void
}) {
  const Icon = ICON_MAP[item.icone] ?? Plug

  return (
    <Card className="border-border/70 flex flex-col shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <div className="bg-muted flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate text-base">{item.nome}</CardTitle>
            <CardDescription className="mt-1 flex flex-wrap gap-1.5">
              <Badge variant="outline">{item.categoria}</Badge>
              <Badge variant={statusVariant(item.status)}>{item.status}</Badge>
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="text-muted-foreground flex-1 space-y-2 text-xs">
        <MetaRow label="Versão" value={item.versao} />
        <MetaRow label="Última conexão" value={formatConnection(item.ultimaConexao)} />
        <MetaRow label="Tipo" value={item.tipo} />
        {!item.ativo ? (
          <p className="text-warning">Registro inativo no cadastro</p>
        ) : null}
        {item.ultimoErro ? (
          <p className="text-destructive line-clamp-2">Erro: {item.ultimoErro}</p>
        ) : null}
      </CardContent>
      <CardFooter className="flex flex-wrap gap-2 border-t pt-4">
        <Button type="button" size="sm" variant="outline" onClick={() => onAction('edit', item.id)}>
          <Wrench className="h-3.5 w-3.5" />
          Editar
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => onAction('test', item.id)}>
          <Link2 className="h-3.5 w-3.5" />
          Testar
        </Button>
        <Button type="button" size="sm" onClick={() => onAction('connect', item.id)}>
          <Plug className="h-3.5 w-3.5" />
          Conectar
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => onAction('disconnect', item.id)}
        >
          <Unplug className="h-3.5 w-3.5" />
          Desconectar
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => onAction('logs', item.id)}>
          <ScrollText className="h-3.5 w-3.5" />
          Logs
        </Button>
      </CardFooter>
    </Card>
  )
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span>{label}</span>
      <span className={cn('text-foreground truncate font-medium')}>{value}</span>
    </div>
  )
}

function statusVariant(
  status: IntegrationStatus,
): 'default' | 'secondary' | 'outline' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'Conectado':
      return 'success'
    case 'Conectando':
      return 'warning'
    case 'Erro':
      return 'destructive'
    case 'Expirado':
      return 'warning'
    case 'Desativado':
      return 'secondary'
    default:
      return 'outline'
  }
}

function formatConnection(value: string | null): string {
  if (!value) return 'Nunca'
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(value))
  } catch {
    return value
  }
}
